import {debounce} from '@github/mini-throttle'
import {type SafeHTMLString, SafeHTMLText} from '@github-ui/safe-html'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {useToastContext} from '@github-ui/toast'
import {Box, Text} from '@primer/react'
import {MarkdownViewer as PrimerMarkdownViewer} from '@primer/react/experimental'
import React, {useCallback, useLayoutEffect, useRef} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS, TEST_IDS} from '../constants'
import {commitUpdateIssueBodyMutation} from '../mutations/update-issue-body-mutation'
import {commitUpdateIssueCommentBodyMutation} from '../mutations/update-issue-comment-body-mutation'

export enum IssueMarkdownContext {
  Issue = 'issue',
  Comment = 'comment',
}

type IssueBodyMarkdownViewerProps = {
  id: string
  context: IssueMarkdownContext
  html: SafeHTMLString
  markdown: string
  viewerCanUpdate: boolean
  dataTestId?: string
  /**
   * Called when the user clicks a link element. This can be used to intercept the click
   * and provide custom routing.
   *
   * Note that this is a native HTML `MouseEvent` and not a `React.ClickEvent`.
   */
  onLinkClick?: (event: MouseEvent) => void
}

export const IssueBodyMarkdownViewer = ({
  id,
  context,
  html,
  markdown,
  viewerCanUpdate,
  onLinkClick,
  dataTestId = TEST_IDS.markdownBody,
}: IssueBodyMarkdownViewerProps) => {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const [isSaving, setIsSaving] = React.useState(false)

  const finishEditing = useCallback(
    (onSuccess: () => void, onError: () => void, error?: string) => {
      setIsSaving(false)
      if (error) {
        onError()
        addToast({
          type: 'error',
          message: error,
        })
      } else {
        onSuccess()
      }
    },
    [addToast],
  )

  const onPerformChange = useCallback(
    (body: string) => {
      const promise = new Promise<void>((resolve, reject) => {
        if (context === IssueMarkdownContext.Comment) {
          setIsSaving(true)
          commitUpdateIssueCommentBodyMutation({
            environment,
            input: {id, body},
            onCompleted: () => finishEditing(resolve, reject),
            onError: () => finishEditing(resolve, reject, 'Could not update issue comment'),
          })
        } else if (context === IssueMarkdownContext.Issue) {
          setIsSaving(true)
          commitUpdateIssueBodyMutation({
            environment,
            input: {issueId: id, body},
            onCompleted: () => finishEditing(resolve, reject),
            onError: () => finishEditing(resolve, reject, 'Could not update issue body'),
          })
        } else {
          throw new Error(`Unknown context: ${context}`)
        }
      })

      return promise
    },
    [context, environment, finishEditing, id],
  )

  // useRef is used here to make sure we don't call onPerformChange with an outdated function
  // in case the issue has changed for example in between
  const onPerformChangeRef = useRef(onPerformChange)
  useLayoutEffect(() => {
    onPerformChangeRef.current = onPerformChange
  }, [onPerformChange])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChange = useCallback(
    debounce((nextValue: string) => onPerformChangeRef.current(nextValue), 500),
    [],
  )

  let innerBody: JSX.Element
  if (html && html.length > 0) {
    // workaround for https://github.com/github/primer/issues/2090 that is preventing the
    // IssueBodyMarkdownViewer component from Primer to render on the servers
    if (IS_SERVER) {
      innerBody = (
        <Box sx={{fontSize: 1, maxWidth: '100%', '& > div > :last-child': {mb: 0}}}>
          <SafeHTMLText html={html} />
        </Box>
      )
    } else {
      innerBody = (
        <PrimerMarkdownViewer
          disabled={!viewerCanUpdate || isSaving}
          dangerousRenderedHTML={{__html: html}}
          markdownValue={markdown}
          onChange={onChange}
          onLinkClick={onLinkClick}
        />
      )
    }
  } else {
    innerBody = (
      <Text sx={{color: 'fg.muted', m: 0, fontStyle: 'italic', fontSize: 1}}>{LABELS.noDescriptionProvided}</Text>
    )
  }

  return (
    <Box
      data-testid={dataTestId}
      className="markdown-body"
      // Make links from project -> project hard navigate if the user chooses to open in same tab
      // https://github.com/github/memex/issues/9202#issuecomment-1085941126
      data-turbolinks="false"
    >
      {innerBody}
    </Box>
  )
}

try{ IssueBodyMarkdownViewer.displayName ||= 'IssueBodyMarkdownViewer' } catch {}